import {Assert, E, SleepAsync, SleepAsyncUntil} from "js-vextensions";
import {remoteUserProcessesBridge} from "../Bridge_RemoteUserProcesses.js";
import {VideoChunk, WebRTCConnectionRequest, WebRTCOfferResponse, WebRTCSignalingMessage, WebRTCSignalingResponse, DataChannelMessage_ClientToServer, DataChannelMessage_ServerToClient, VideoChunkInfo, VideoRequest} from "./Types.js";
import {AbortError, CancelableContext, GetPromiseAndResolveRejectFuncs} from "../../General/Async.js";
import {AssertNotify} from "web-vcore";
import {ErrToStr} from "../../General/Errors.js";

export class VideoClip {
	constructor(videoPath: string) {
		this.videoPath = videoPath;
	}
	videoPath: string;
	totalSize: number|n;
	chunks: VideoChunk[] = [];
	blob: Blob|n;

	onComplete_listeners = [] as Array<(clip: VideoClip)=>void>;
	OnComplete() {
		this.chunks.sort((a, b)=>a.offset - b.offset);
		const chunks = this.chunks.map(chunk=>new Uint8Array(chunk.data));
		this.blob = new Blob(chunks, {type: "video/mp4"});
		console.log("Video assembled and ready to play", this.blob);

		/*const videoUrl = URL.createObjectURL(videoBlob);
		const videoElement = document.createElement("video");
		videoElement.src = videoUrl;
		videoElement.autoplay = true;
		videoElement.loop = true;*/

		for (const listener of this.onComplete_listeners) {
			listener(this);
		}
	}
}

export class RTCClient {
	private peerConnection: RTCPeerConnection|n;
	private dataChannel: RTCDataChannel|n;
	private currentSessionId: string|n;

	private clipsBeingReceived = new Map<string, VideoClip>();
	// internal (while piecing together next chunk)
	currentChunkInfo: VideoChunkInfo|n;

	constructor() {
		this.SetupBridgeFunctions();
	}

	private SetupBridgeFunctions(): void {
		// Register function for server to send WebRTC signaling data back to us
		remoteUserProcessesBridge.RegisterFunction("ReceiveWebRTCSignaling", async(signaling: WebRTCSignalingMessage): Promise<WebRTCSignalingResponse>=>{
			return await this.HandleIncomingSignaling(signaling);
		});
	}
	private async HandleIncomingSignaling(signaling: WebRTCSignalingMessage): Promise<WebRTCSignalingResponse> {
		if (!this.peerConnection) {
			return {success: false, error: "No peer connection available"};
		}

		try {
			if (signaling.candidate) {
				await this.peerConnection.addIceCandidate(signaling.candidate.candidate);
				console.log("Added ICE candidate from server");
			}
			return {success: true};
		} catch (error) {
			return {success: false, error: ErrToStr(error)};
		}
	}

	private SetupConnectionMonitoring(): void {
		if (!this.peerConnection) return;
		this.peerConnection.addEventListener("connectionstatechange", (): void=>{
			console.log("Connection state:", this.peerConnection?.connectionState);
			if (this.peerConnection?.connectionState === "failed" ||
				this.peerConnection?.connectionState === "disconnected") {
				console.log("Connection lost, cleaning up... (so new connection can happen for next request)");
				this.Cleanup();
			}
		});
	}
	private Cleanup(): void {
		this.currentSessionId = null;
		this.dataChannel = null;
		this.peerConnection = null;
		this.clipsBeingReceived.clear();
		this.currentChunkInfo = null;
	}

	async EnsureConnectionCreated(): Promise<void> {
		// if session not even created yet, create it
		if (this.currentSessionId == null) {
			const connectionAttempt_startTime = Date.now();
			try {
				const ctx = new CancelableContext().CancelAfterXIfSequenceNotCompleted(10000);
				this.currentSessionId = "vr-session-" + Date.now();

				this.peerConnection = new RTCPeerConnection({
					iceServers: [{urls: "stun:stun.l.google.com:19302"}]
				});
				this.SetupConnectionMonitoring();

				// Handle incoming data channel
				const dataChannelFormation_promise = await GetPromiseAndResolveRejectFuncs<void>();
				this.peerConnection.ondatachannel = (event: RTCDataChannelEvent): void=>{
					console.log("DataChannel formed");
					this.dataChannel = event.channel;
					this.dataChannel.onmessage = (event: MessageEvent<string | ArrayBuffer>): void=>{
						this.HandleMessageFromServer(event.data);
					};
					dataChannelFormation_promise.resolve();
				};
				// Handle ICE candidates
				this.peerConnection.onicecandidate = async(event: RTCPeerConnectionIceEvent): Promise<void>=>{
					if (event.candidate && this.currentSessionId) {
						console.log("onicecandidate:", event.candidate, event, this.currentSessionId);
						await ctx.Await(this.SendWebRTCSignaling({
							sessionId: this.currentSessionId,
							candidate: {candidate: event.candidate},
						}));
					}
				};

				// Request connection from phone
				console.log("RequestRTCConnection...");
				const offer = await ctx.Await(this.RequestRTCConnection({
					sessionId: this.currentSessionId,
				}));

				// Handle the offer
				console.log("setRemoteDescription...");
				await ctx.Await(this.peerConnection.setRemoteDescription(offer.offer));
				console.log("createAnswer...");
				const answer = await ctx.Await(this.peerConnection.createAnswer());
				console.log("setLocalDescription...");
				await ctx.Await(this.peerConnection.setLocalDescription(answer));

				// Send answer back to phone
				console.log("SendWebRTCSignaling...");
				await ctx.Await(this.SendWebRTCSignaling({
					sessionId: this.currentSessionId,
					answer: {answer},
				}));
				console.log("SendWebRTCSignaling done");

				// note: additional wait time occurs here, because resultPromise.resolve() is not called until the dataChannel is set (in ondatachannel handler above)
				await dataChannelFormation_promise.promise;
				console.log("Data-channel formation done");
			} catch (error) {
				if (error instanceof AbortError) {
					console.error(`WebRTC connection timed out; retrying shortly.`);
					AssertNotify(false, `WebRTC connection timed out; retrying shortly.`);
				} else {
					console.error(`WebRTC connection failed; retrying shortly. Error:`, error);
					AssertNotify(false, `WebRTC connection failed; retrying shortly. Error: ${error}`);
				}
				this.Cleanup();

				const timeSinceAttemptStart = Date.now() - connectionAttempt_startTime;
				const waitTimeNeededToReach10sBetweenAttempts = (10000 - timeSinceAttemptStart).KeepAtLeast(0);
				await SleepAsync(waitTimeNeededToReach10sBetweenAttempts); // wait to ensure at least 10 seconds between connection attempts
				this.EnsureConnectionCreated(); // retry connection setup
			}
		} else {
			// else, simply wait until this.dataChannel is set
			while (!this.dataChannel) {
				await SleepAsync(100);
			}
		}
	}
	private async RequestRTCConnection(request: WebRTCConnectionRequest) {
		return await remoteUserProcessesBridge.Call("RequestRTCConnection", request) as WebRTCOfferResponse;
	}
	private async SendWebRTCSignaling(signaling: WebRTCSignalingMessage) {
		return await remoteUserProcessesBridge.Call("HandleWebRTCSignalingFromClient", signaling) as WebRTCSignalingResponse;
	}

	private async RequestVideoStream(request: VideoRequest): Promise<VideoClip> {
		Assert(this.dataChannel, "Data channel not available. Ensure connection is established first.");

		//Assert(!this.clipsBeingReceived.has(request.videoPath), `Video data already in progress for this path: ${request.videoPath}`);
		// if clip already being received, wait for it to finish and return it
		// (this can happen, eg. if two requests for the same video are made in quick succession -- not necessarily an error, since two frames can randomly select the same video)
		if (this.clipsBeingReceived.has(request.videoPath)) {
			const existingClip = this.clipsBeingReceived.get(request.videoPath)!;
			while (existingClip.blob == null) {
				await SleepAsync(100);
			}
			return existingClip;
		}

		const clip = new VideoClip(request.videoPath);
		this.clipsBeingReceived.set(request.videoPath, clip);

		this.dataChannel.send(JSON.stringify(new DataChannelMessage_ClientToServer({
			videoRequest: request,
		})));
		console.log(`Requested video stream. @request:`, request);

		return clip;
	}

	private HandleMessageFromServer(data: string | ArrayBuffer): void {
		if (typeof data === "string") {
			try {
				const message = JSON.parse(data) as DataChannelMessage_ServerToClient;
				const {videoStart, videoChunkInfo} = message;
				if (videoStart) {
					const clip = this.clipsBeingReceived.get(videoStart.videoPath);
					Assert(clip, `Entry for clip-being-received not found, for video-path: ${videoStart.videoPath}`);
					clip.totalSize = videoStart.totalSize;
					console.log(`Starting video receive: ${videoStart.totalSize} bytes`);
				} else if (videoChunkInfo) {
					const clip = this.clipsBeingReceived.get(videoChunkInfo.videoPath);
					Assert(clip, `Entry for clip-being-received not found, for video-path: ${videoChunkInfo.videoPath}`);
					this.currentChunkInfo = videoChunkInfo;
				}
			} catch (error) {
				console.error("Error parsing video message:", error);
			}
		} else {
			//console.log(`Received non-string data in handleVideoData func:`, data);
			const chunkInfo = this.currentChunkInfo;
			if (chunkInfo) {
				const clip = this.clipsBeingReceived.get(chunkInfo.videoPath);
				Assert(clip, `Entry for clip-being-received not found, for video-path: ${chunkInfo.videoPath}`);

				// store the new chunk-data, and call OnComplete if this is the last chunk
				clip.chunks.push({
					offset: chunkInfo.offset,
					data: data,
				});
				if (chunkInfo.isLast) {
					this.clipsBeingReceived.delete(chunkInfo.videoPath);
					clip.OnComplete();
				}

				this.currentChunkInfo = null;
			} else {
				console.warn("Received video data without currentChunk_infoFromServer set first (something is wrong if it's not!); ignoring data.");
			}
		}
	}

	async GetVideoClip(request: VideoRequest) {
		await this.EnsureConnectionCreated();
		const clip = await this.RequestVideoStream(request);
		return new Promise<VideoClip>((resolve, reject)=>{
			clip.onComplete_listeners.push(()=>resolve(clip));
		});
	}
}

export const rtcClient = new RTCClient();